import { useEffect, useMemo, useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Link
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch, useSelector } from 'react-redux';
import { exportAuditTrail, fetchAuditTrail } from '../store/slices/auditTrailSlice';

const columns = [
  { id: 'created_datetime', label: 'Datetime' },
  { id: 'admin', label: 'Admin' },
  { id: 'activity_desc', label: 'Activity', sortable: false }
];

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString();
};

const formatDateForInput = (date) => date.toISOString().split('T')[0];

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return {
    start: formatDateForInput(start),
    end: formatDateForInput(end)
  };
};

const AuditTrailPage = () => {
  const dispatch = useDispatch();
  const {
    items,
    meta,
    loading,
    error,
    query,
    exportStatus,
    exportError
  } = useSelector((state) => state.auditTrail);

  const { start: defaultStartDate, end: defaultEndDate } = useMemo(
    () => getDefaultDateRange(),
    []
  );

  const [startDate, setStartDate] = useState(() => query.startDate || defaultStartDate);
  const [endDate, setEndDate] = useState(() => query.endDate || defaultEndDate);
  const [search, setSearch] = useState(query.search || '');
  const [debouncedSearch, setDebouncedSearch] = useState(query.search || '');
  const [sortBy, setSortBy] = useState(query.sortBy || 'created_datetime');
  const [sortDirection, setSortDirection] = useState(query.sortDirection || 'desc');
  const [page, setPage] = useState(Math.max((query.page || 1) - 1, 0));
  const [rowsPerPage, setRowsPerPage] = useState(query.pageSize || 10);
  const [dense, setDense] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  const openMenu = Boolean(menuAnchor);

  const isDateRangeInvalid =
    startDate && endDate && new Date(startDate) > new Date(endDate);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (isDateRangeInvalid) {
      return;
    }

    dispatch(
      fetchAuditTrail({
        page: page + 1,
        pageSize: rowsPerPage,
        startDate,
        endDate,
        search: debouncedSearch.trim(),
        sortBy,
        sortDirection
      })
    );
  }, [
    dispatch,
    page,
    rowsPerPage,
    startDate,
    endDate,
    debouncedSearch,
    sortBy,
    sortDirection,
    isDateRangeInvalid
  ]);

  useEffect(() => {
    if (typeof query.page === 'number') {
      const zeroBasedPage = Math.max(query.page - 1, 0);
      setPage((prev) => (prev === zeroBasedPage ? prev : zeroBasedPage));
    }
    if (typeof query.pageSize === 'number') {
      setRowsPerPage((prev) => (prev === query.pageSize ? prev : query.pageSize));
    }
  }, [query.page, query.pageSize]);

  useEffect(() => {
    setExpandedRow(null);
  }, [items]);

  const handleSort = (columnId) => {
    if (columnId === 'activity_desc') return;
    if (sortBy === columnId) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(columnId);
      setSortDirection('asc');
    }
    setPage(0);
  };

  const handleDownload = () => {
    setMenuAnchor(null);
    if (isDateRangeInvalid) {
      return;
    }
    dispatch(
      exportAuditTrail({
        startDate,
        endDate,
        search: debouncedSearch.trim(),
        sortBy,
        sortDirection
      })
    );
  };

  const tableRows = useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        created_datetime: formatDateTime(item.created_datetime),
        admin: {
          name: item.admin?.name ?? item.admin_name,
          email: item.admin?.email ?? item.admin_email
        },
        activity_desc: item.activity_desc || ''
      })),
    [items]
  );

  const emptyState = (
    <Box sx={{ textAlign: 'center', py: 5 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        No records found
      </Typography>
      <Typography color="text.secondary">
        Try adjusting the filters to find the audit events you need.
      </Typography>
    </Box>
  );

  return (
    <Stack spacing={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
      >
        <Box sx={{ minWidth: 0 }}>
          <Breadcrumbs
            aria-label="breadcrumb"
            separator="/"
            sx={{ fontSize: 12, mb: 1, color: 'text.secondary' }}
          >
            <Link underline="hover" color="inherit" href="#">
              Dashboard
            </Link>
            <Link underline="hover" color="inherit" href="#">
              Management
            </Link>
            <Typography color="text.primary">Audit Trail</Typography>
          </Breadcrumbs>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Audit Trail
          </Typography>
          <Typography color="text.secondary">
            Track administrator activity across the platform.
          </Typography>
        </Box>
        <IconButton
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={openMenu}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            elevation: 6,
            sx: {
              boxShadow: '0px 22px 45px rgba(15, 23, 42, 0.18)',
              border: '1px solid rgba(148, 163, 184, 0.16)',
              background:
                'radial-gradient(circle at top right, rgba(14, 165, 233, 0.24), transparent 55%), #ffffff',
              borderRadius: 1,
              mt: 1
            }
          }}
        >
          <MenuItem onClick={handleDownload} disabled={exportStatus === 'loading'}>
            <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
            {exportStatus === 'loading' ? 'Preparing...' : 'Download Excel'}
          </MenuItem>
        </Menu>
      </Stack>

      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ md: 'center' }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ width: { xs: '100%', md: 'auto' }, flexShrink: 0 }}
            >
              <TextField
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(event) => {
                  setPage(0);
                  setStartDate(event.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '100%', sm: 200 } }}
              />
              <TextField
                type="date"
                label="End Date"
                value={endDate}
                onChange={(event) => {
                  setPage(0);
                  setEndDate(event.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: { xs: '100%', sm: 200 } }}
              />
            </Stack>
            <TextField
              value={search}
              onChange={(event) => {
                setPage(0);
                setSearch(event.target.value);
              }}
              placeholder="Search by name, email, or activity"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
              sx={{
                width: { xs: '100%', md: 'auto' },
                flexGrow: 1,
                minWidth: { md: 240, lg: 320 }
              }}
            />
          </Stack>

          {isDateRangeInvalid && (
            <Alert severity="error">End date must be on or after start date.</Alert>
          )}
          {error && !loading && <Alert severity="error">{error}</Alert>}
          {exportError && <Alert severity="error">{exportError}</Alert>}

          {loading && <LinearProgress />}

          <Box
            sx={{
              width: '100%',
              overflowX: 'auto',
              border: '1px solid rgba(148, 163, 184, 0.16)',
              borderRadius: 1,
              backgroundColor: 'background.paper'
            }}
          >
            <TableContainer>
              <Table size={dense ? 'small' : 'medium'} sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        sortDirection={sortBy === column.id ? sortDirection : false}
                      >
                        {column.sortable === false ? (
                          column.label
                        ) : (
                          <TableSortLabel
                            active={sortBy === column.id}
                            direction={sortBy === column.id ? sortDirection : 'asc'}
                            onClick={() => handleSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableRows.map((row) => (
                    <TableRow
                      hover
                      key={row.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(15, 23, 42, 0.03)'
                        }
                      }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.created_datetime}</TableCell>
                      <TableCell>
                        <Typography fontWeight={600}>{row.admin.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {row.admin.email || 'No email'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {row.activity_desc && row.activity_desc.length > 160
                            ? `${row.activity_desc.slice(0, 160)}...`
                            : row.activity_desc || '-'}
                        </Typography>
                        {row.activity_desc && row.activity_desc.length > 160 && (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setExpandedRow(row)}
                            sx={{ mt: 0.5, textTransform: 'none', px: 0 }}
                          >
                            View More
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!tableRows.length && !loading && (
                    <TableRow>
                      <TableCell colSpan={columns.length}>{emptyState}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch checked={dense} onChange={(event) => setDense(event.target.checked)} />
              <Typography variant="body2">Dense</Typography>
            </Stack>

            <TablePagination
              component="div"
              count={meta?.total_items ?? 0}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                const nextRows = parseInt(event.target.value, 10);
                setRowsPerPage(nextRows);
                setPage(0);
              }}
              sx={{
                '& .MuiTablePagination-toolbar': {
                  px: { xs: 0, sm: 2 }
                }
              }}
            />
          </Stack>
        </Stack>
      </Paper>

      <Dialog
        open={Boolean(expandedRow)}
        onClose={() => setExpandedRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Activity Detail</DialogTitle>
        <DialogContent dividers>
          <DialogContentText component="div" sx={{ whiteSpace: 'pre-wrap' }}>
            {expandedRow?.activity_desc}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpandedRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default AuditTrailPage;


